import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Plus, Trash2, Save, Pill, Check, Edit2, AlertTriangle, Stethoscope, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = ['daily', 'twice daily', 'three times daily', 'weekly', 'as needed'];
const TIMES_OF_DAY = ['morning', 'afternoon', 'evening', 'bedtime'];

export default function SupplementTracker() {
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [todayLogs, setTodayLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    
    // Form state
    const [name, setName] = useState('');
    const [dosage, setDosage] = useState('');
    const [frequency, setFrequency] = useState('daily');
    const [timeOfDay, setTimeOfDay] = useState('morning');
    const [withFood, setWithFood] = useState(true);
    const [itemType, setItemType] = useState('supplement');
    const [prescriber, setPrescriber] = useState('');
    const [pharmacy, setPharmacy] = useState('');
    const [refillDate, setRefillDate] = useState('');
    const [interactions, setInteractions] = useState([]);
    const [newInteraction, setNewInteraction] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/supplements`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setItems(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch items:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchTodayLogs = useCallback(async () => {
        try {
            const today = format(new Date(), 'yyyy-MM-dd');
            const res = await fetch(`${API_URL}/api/supplements/logs/${today}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                const data = await res.json();
                setTodayLogs(data || []);
            }
        } catch (error) {
            console.error('Failed to fetch logs:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchItems();
        fetchTodayLogs();
    }, [fetchItems, fetchTodayLogs]);

    const resetForm = () => {
        setName('');
        setDosage('');
        setFrequency('daily');
        setTimeOfDay('morning');
        setWithFood(true);
        setItemType('supplement');
        setPrescriber('');
        setPharmacy('');
        setRefillDate('');
        setInteractions([]);
        setNewInteraction('');
        setNotes('');
        setEditingId(null);
    };

    const addInteraction = () => {
        if (newInteraction.trim() && !interactions.includes(newInteraction.trim())) {
            setInteractions([...interactions, newInteraction.trim()]);
            setNewInteraction('');
        }
    };

    const removeInteraction = (item) => {
        setInteractions(interactions.filter(i => i !== item));
    };

    const saveItem = async () => {
        if (!name.trim() || !dosage.trim()) {
            toast.error('Please enter name and dosage');
            return;
        }

        setSaving(true);
        try {
            const url = editingId 
                ? `${API_URL}/api/supplements/${editingId}`
                : `${API_URL}/api/supplements`;
            
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name,
                    dosage,
                    frequency,
                    time_of_day: timeOfDay,
                    with_food: withFood,
                    item_type: itemType,
                    prescriber: prescriber || null,
                    pharmacy: pharmacy || null,
                    refill_date: refillDate || null,
                    interactions: interactions.length > 0 ? interactions : null,
                    active: true,
                    notes: notes || null
                })
            });

            if (res.ok) {
                toast.success(editingId ? 'Item updated' : 'Item added');
                setShowAdd(false);
                resetForm();
                fetchItems();
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save item');
        } finally {
            setSaving(false);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Delete this item?')) return;

        try {
            const res = await fetch(`${API_URL}/api/supplements/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Item deleted');
                fetchItems();
            }
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const toggleActive = async (item) => {
        try {
            const res = await fetch(`${API_URL}/api/supplements/${item.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...item,
                    active: !item.active
                })
            });

            if (res.ok) {
                toast.success(item.active ? 'Item paused' : 'Item resumed');
                fetchItems();
            }
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const logTaken = async (itemId, taken) => {
        try {
            const today = format(new Date(), 'yyyy-MM-dd');
            const now = format(new Date(), 'HH:mm');
            
            const res = await fetch(`${API_URL}/api/supplements/log`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    supplement_id: itemId,
                    date: today,
                    taken,
                    time_taken: taken ? now : null
                })
            });

            if (res.ok) {
                toast.success(taken ? 'Marked as taken' : 'Marked as skipped');
                fetchTodayLogs();
            }
        } catch (error) {
            toast.error('Failed to log');
        }
    };

    const editItem = (item) => {
        setEditingId(item.id);
        setName(item.name);
        setDosage(item.dosage);
        setFrequency(item.frequency);
        setTimeOfDay(item.time_of_day || 'morning');
        setWithFood(item.with_food ?? true);
        setItemType(item.item_type || 'supplement');
        setPrescriber(item.prescriber || '');
        setPharmacy(item.pharmacy || '');
        setRefillDate(item.refill_date || '');
        setInteractions(item.interactions || []);
        setNotes(item.notes || '');
        setShowAdd(true);
    };

    const isTakenToday = (itemId) => {
        return todayLogs.some(log => log.supplement_id === itemId && log.taken);
    };

    // Filter items based on tab
    const filteredItems = items.filter(item => {
        if (activeTab === 'all') return item.active;
        if (activeTab === 'supplements') return item.active && (item.item_type === 'supplement' || !item.item_type);
        if (activeTab === 'medications') return item.active && item.item_type === 'medication';
        return true;
    });

    const inactiveItems = items.filter(item => !item.active);
    const activeSupplements = items.filter(i => i.active && (i.item_type === 'supplement' || !i.item_type));
    const activeMedications = items.filter(i => i.active && i.item_type === 'medication');
    const takenCount = filteredItems.filter(i => isTakenToday(i.id)).length;

    // Check for interactions
    const getInteractionWarnings = (item) => {
        if (!item.interactions?.length) return [];
        return item.interactions.filter(interaction => 
            items.some(other => 
                other.active && 
                other.id !== item.id && 
                other.name.toLowerCase().includes(interaction.toLowerCase())
            )
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Supplements & Medications</h1>
                    <p className="text-muted-foreground mt-1">Manage your supplements and medications in one place</p>
                </div>
                <div className="flex items-center gap-3">
                    <TimelineButtons />
                    <Button onClick={() => { resetForm(); setShowAdd(true); }} data-testid="add-item-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Item
                    </Button>
                </div>
            </div>

            {/* Progress Card */}
            {filteredItems.length > 0 && (
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-200">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-500 rounded-lg">
                                    <Pill className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">Today's Progress</p>
                                    <p className="text-2xl font-bold">{takenCount} / {filteredItems.length}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-muted-foreground">
                                    {Math.round((takenCount / filteredItems.length) * 100)}% complete
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="all">All Active ({items.filter(i => i.active).length})</TabsTrigger>
                    <TabsTrigger value="supplements">
                        <Pill className="w-4 h-4 mr-1" />
                        Supplements ({activeSupplements.length})
                    </TabsTrigger>
                    <TabsTrigger value="medications">
                        <Stethoscope className="w-4 h-4 mr-1" />
                        Medications ({activeMedications.length})
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Add/Edit Form */}
                    {showAdd && (
                        <Card className="border-2 border-indigo-200 dark:border-indigo-800">
                            <CardHeader>
                                <CardTitle>{editingId ? 'Edit Item' : 'Add Supplement or Medication'}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Type Selection */}
                                <div className="flex gap-2">
                                    <Button
                                        variant={itemType === 'supplement' ? 'default' : 'outline'}
                                        onClick={() => setItemType('supplement')}
                                        className="flex-1"
                                    >
                                        <Pill className="w-4 h-4 mr-2" />
                                        Supplement
                                    </Button>
                                    <Button
                                        variant={itemType === 'medication' ? 'default' : 'outline'}
                                        onClick={() => setItemType('medication')}
                                        className="flex-1"
                                    >
                                        <Stethoscope className="w-4 h-4 mr-2" />
                                        Medication
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Name</label>
                                        <Input
                                            placeholder={itemType === 'medication' ? "e.g., Methotrexate" : "e.g., Vitamin D3"}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            data-testid="item-name-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Dosage</label>
                                        <Input
                                            placeholder="e.g., 5000 IU or 10mg"
                                            value={dosage}
                                            onChange={(e) => setDosage(e.target.value)}
                                            data-testid="item-dosage-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Frequency</label>
                                        <Select value={frequency} onValueChange={setFrequency}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {FREQUENCIES.map(f => (
                                                    <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Time of Day</label>
                                        <Select value={timeOfDay} onValueChange={setTimeOfDay}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TIMES_OF_DAY.map(t => (
                                                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Medication-specific fields */}
                                {itemType === 'medication' && (
                                    <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">Prescriber</label>
                                            <Input
                                                placeholder="Dr. Smith"
                                                value={prescriber}
                                                onChange={(e) => setPrescriber(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium mb-2 block">Pharmacy</label>
                                            <Input
                                                placeholder="CVS, Walgreens, etc."
                                                value={pharmacy}
                                                onChange={(e) => setPharmacy(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-sm font-medium mb-2 block">Next Refill Date</label>
                                            <Input
                                                type="date"
                                                value={refillDate}
                                                onChange={(e) => setRefillDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Interactions */}
                                <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
                                    <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                                        Negative Interactions
                                    </label>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        List items this should NOT be taken with
                                    </p>
                                    <div className="flex gap-2 mb-2">
                                        <Input
                                            placeholder="e.g., Iron, Calcium, Warfarin..."
                                            value={newInteraction}
                                            onChange={(e) => setNewInteraction(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && addInteraction()}
                                        />
                                        <Button variant="outline" onClick={addInteraction}>Add</Button>
                                    </div>
                                    {interactions.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {interactions.map((item, idx) => (
                                                <Badge key={idx} variant="destructive" className="gap-1">
                                                    {item}
                                                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeInteraction(item)} />
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                    <label className="text-sm font-medium">Take with food</label>
                                    <Switch checked={withFood} onCheckedChange={setWithFood} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium mb-2 block">Notes</label>
                                    <Textarea
                                        placeholder="Any additional notes..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={2}
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <Button onClick={saveItem} disabled={saving} className="flex-1" data-testid="save-item-btn">
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving ? 'Saving...' : editingId ? 'Update' : 'Add Item'}
                                    </Button>
                                    <Button variant="outline" onClick={() => { setShowAdd(false); resetForm(); }}>Cancel</Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Active Items */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Active Items</CardTitle>
                            <CardDescription>Mark as taken when you take each item</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Pill className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>No active items</p>
                                    <Button variant="link" onClick={() => setShowAdd(true)}>Add your first item</Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredItems.map((item) => {
                                        const taken = isTakenToday(item.id);
                                        const warnings = getInteractionWarnings(item);
                                        return (
                                            <div
                                                key={item.id}
                                                className={`p-4 rounded-xl border transition-colors ${
                                                    taken 
                                                        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800' 
                                                        : warnings.length > 0 
                                                            ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
                                                            : 'bg-muted/30'
                                                }`}
                                                data-testid={`item-${item.id}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-medium">{item.name}</span>
                                                            <Badge variant="outline" className="text-xs">{item.dosage}</Badge>
                                                            <Badge 
                                                                className={item.item_type === 'medication' 
                                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
                                                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                                }
                                                            >
                                                                {item.item_type === 'medication' ? 'Rx' : 'Supp'}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                            <span className="capitalize">{item.frequency}</span>
                                                            <span>•</span>
                                                            <span className="capitalize">{item.time_of_day}</span>
                                                            {item.with_food && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>With food</span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {warnings.length > 0 && (
                                                            <div className="flex items-center gap-1 mt-2 text-xs text-amber-600 dark:text-amber-400">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                <span>Interaction warning: {warnings.join(', ')}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {!taken ? (
                                                            <Button
                                                                size="sm"
                                                                className="bg-emerald-600 hover:bg-emerald-700"
                                                                onClick={() => logTaken(item.id, true)}
                                                                data-testid={`take-${item.id}`}
                                                            >
                                                                <Check className="w-4 h-4 mr-1" />
                                                                Take
                                                            </Button>
                                                        ) : (
                                                            <Badge className="bg-emerald-500">
                                                                <Check className="w-3 h-3 mr-1" />
                                                                Taken
                                                            </Badge>
                                                        )}
                                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => editItem(item)}>
                                                            <Edit2 className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteItem(item.id)}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Inactive Items */}
                    {inactiveItems.length > 0 && (
                        <Card className="opacity-75">
                            <CardHeader>
                                <CardTitle className="text-lg">Paused Items</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {inactiveItems.map((item) => (
                                        <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                                            <div>
                                                <span className="font-medium">{item.name}</span>
                                                <span className="text-sm text-muted-foreground ml-2">{item.dosage}</span>
                                            </div>
                                            <Button variant="outline" size="sm" onClick={() => toggleActive(item)}>
                                                Resume
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <div className="space-y-6">
                    <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg">💊 Tips</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Take fat-soluble vitamins (A, D, E, K) with food</p>
                            <p>• Space calcium and iron supplements apart</p>
                            <p>• Take B vitamins in the morning</p>
                            <p>• Magnesium can help with sleep</p>
                            <p>• Always consult your doctor</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                Common Interactions
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2 text-muted-foreground">
                            <p>• Vitamin K with blood thinners</p>
                            <p>• St. John's Wort with many meds</p>
                            <p>• Iron with thyroid medication</p>
                            <p>• Calcium with certain antibiotics</p>
                            <p>• Grapefruit with various medications</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
